import React, { useState } from 'react';
import styled from 'styled-components';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  Copy,
  FileText,
  MapPin,
  Send,
  Shield
} from 'lucide-react';
import { usePlainte, TYPES_PLAINTE, LOCALITES } from '../hooks/usePlaintes';

const PageContainer = styled.div`
  padding-top: 90px;
  min-height: 100vh;
  background: var(--background-light);
`;

const Hero = styled.section`
  background: linear-gradient(135deg, var(--primary-blue) 0%, #004d8f 100%);
  padding: 4rem 0;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }
`;

const HeroContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 2rem;
  position: relative;
  z-index: 1;
  text-align: center;
`;

const HeroTitle = styled.h1`
  font-family: var(--font-secondary);
  font-size: 3rem;
  color: var(--white);
  margin-bottom: 1rem;
  font-weight: 800;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  max-width: 760px;
  margin: 0 auto;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 2rem 4rem;
`;

const FormCard = styled.div`
  background: var(--white);
  border-radius: var(--radius-xl);
  padding: 2.5rem;
  box-shadow: var(--shadow-lg);
  margin-top: -2rem;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const InfoBox = styled.div`
  background: #eff6ff;
  border-left: 4px solid var(--primary-blue);
  padding: 1rem 1.25rem;
  border-radius: var(--radius-md);
  margin-bottom: 2rem;
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;

  svg {
    color: var(--primary-blue);
    flex-shrink: 0;
    margin-top: 0.15rem;
  }

  p {
    color: #1e40af;
    font-size: 0.95rem;
    line-height: 1.6;
  }
`;

const SectionTitle = styled.h2`
  font-family: var(--font-secondary);
  font-size: 1.45rem;
  color: var(--text-dark);
  margin-bottom: 1.4rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    color: var(--primary-blue);
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.25rem;
  margin-bottom: 1.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &.full-width {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  font-weight: 600;
  color: var(--text-dark);
  font-size: 0.95rem;

  span {
    color: var(--secondary-red);
  }
`;

const Input = styled.input`
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const Select = styled.select`
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  background: var(--white);
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const TextArea = styled.textarea`
  padding: 0.875rem 1rem;
  border: 2px solid var(--border-light);
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-height: 180px;
  resize: vertical;
  font-family: inherit;
  transition: var(--transition);

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  width: 100%;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, var(--primary-blue), #004d8f);
  color: var(--white);
  border-radius: var(--radius-md);
  font-size: 1.05rem;
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 38, 100, 0.3);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

const SuccessCard = styled.div`
  background: linear-gradient(135deg, #059669, #10b981);
  color: var(--white);
  border-radius: var(--radius-xl);
  padding: 3rem 2rem;
  text-align: center;
  margin-top: -2rem;
  position: relative;
  z-index: 2;

  svg {
    margin-bottom: 1rem;
  }

  h2 {
    font-family: var(--font-secondary);
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    opacity: 0.95;
    margin-bottom: 1.25rem;
  }
`;

const ReferenceBox = styled.div`
  background: rgba(255, 255, 255, 0.2);
  border: 2px dashed rgba(255, 255, 255, 0.5);
  border-radius: var(--radius-lg);
  padding: 1.5rem;
  margin: 1.5rem 0;

  .label {
    font-size: 0.9rem;
    opacity: 0.85;
    margin-bottom: 0.5rem;
  }

  .reference {
    font-family: monospace;
    font-size: 2rem;
    font-weight: 800;
    letter-spacing: 2px;
  }
`;

const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  padding: 0.75rem 1.5rem;
  background: var(--white);
  color: var(--primary-blue);
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: var(--accent-gold);
  }
`;

const Signalement = () => {
  const { createPlainte } = usePlainte();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reference, setReference] = useState('');
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    localite: '',
    type: '',
    dateFaits: '',
    objet: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const payload = {
      ...formData,
      prenom: 'Anonyme',
      nom: '',
      email: '',
      anonyme: true,
      modeSoumission: 'signalement'
    };

    const result = await createPlainte(payload);

    if (result.success) {
      setReference(result.reference);
      setSubmitted(true);
    } else {
      alert('Erreur: ' + result.error);
    }

    setIsSubmitting(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(reference);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (submitted) {
    return (
      <PageContainer>
        <Hero>
          <HeroContent>
            <HeroTitle>Signalement envoyé</HeroTitle>
            <HeroSubtitle>
              Votre signalement anonyme a été enregistré.
            </HeroSubtitle>
          </HeroContent>
        </Hero>
        <Container>
          <SuccessCard>
            <CheckCircle size={64} />
            <h2>Merci pour votre vigilance</h2>
            <p>
              Votre signalement sera analysé par la Médiature. Aucune information
              d'identité n'est demandée sur cette page.
            </p>

            <ReferenceBox>
              <div className="label">Numéro de référence</div>
              <div className="reference">{reference}</div>
            </ReferenceBox>

            <p>Conservez ce numéro pour suivre l'évolution du dossier.</p>

            <CopyButton onClick={handleCopy}>
              {copied ? 'Copié !' : <><Copy size={18} /> Copier la référence</>}
            </CopyButton>
          </SuccessCard>
        </Container>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Hero>
        <HeroContent>
          <HeroTitle>Faire un Signalement</HeroTitle>
          <HeroSubtitle>
            Cette page permet de transmettre un signalement de manière anonyme.
          </HeroSubtitle>
        </HeroContent>
      </Hero>
      <Container>
        <FormCard>
          <InfoBox>
            <Shield size={22} />
            <p>
              Ce formulaire est anonyme : aucun nom ni e-mail n'est demandé.
              Évitez d'écrire des informations personnelles dans la description.
            </p>
          </InfoBox>

          <InfoBox>
            <AlertTriangle size={22} />
            <p>
              Les champs marqués d'un astérisque (*) sont obligatoires.
              Après envoi, un numéro de référence sera généré pour le suivi.
            </p>
          </InfoBox>

          <form onSubmit={handleSubmit}>
            <SectionTitle>
              <FileText size={24} />
              Détails du signalement
            </SectionTitle>

            <FormGrid>
              <FormGroup>
                <Label>
                  <MapPin size={16} style={{ display: 'inline', marginRight: '0.3rem' }} />
                  Localité <span>*</span>
                </Label>
                <Select
                  value={formData.localite}
                  onChange={(e) => setFormData({ ...formData, localite: e.target.value })}
                  required
                >
                  <option value="">Sélectionnez une localité</option>
                  {LOCALITES.map((loc) => (
                    <option key={loc} value={loc}>{loc}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>Type de signalement <span>*</span></Label>
                <Select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="">Sélectionnez le type</option>
                  {TYPES_PLAINTE.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label>
                  <Calendar size={16} style={{ display: 'inline', marginRight: '0.3rem' }} />
                  Date des faits
                </Label>
                <Input
                  type="date"
                  value={formData.dateFaits}
                  onChange={(e) => setFormData({ ...formData, dateFaits: e.target.value })}
                />
              </FormGroup>

              <FormGroup className="full-width">
                <Label>Objet <span>*</span></Label>
                <Input
                  type="text"
                  value={formData.objet}
                  onChange={(e) => setFormData({ ...formData, objet: e.target.value })}
                  placeholder="Résumé bref du signalement"
                  required
                />
              </FormGroup>

              <FormGroup className="full-width">
                <Label>Description détaillée <span>*</span></Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez les faits, le contexte et les éléments utiles au traitement."
                  required
                />
              </FormGroup>
            </FormGrid>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                'Envoi en cours...'
              ) : (
                <>
                  <Send size={20} />
                  Envoyer le signalement anonyme
                </>
              )}
            </SubmitButton>
          </form>
        </FormCard>
      </Container>
    </PageContainer>
  );
};

export default Signalement;
